import axios from "axios";
import { baseUrl } from "../config/apiConfig";
import { getDataFromLocalStorage } from "../util/localStorage";

const apiClient = axios.create({
  baseURL: baseUrl,
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["ngrok-skip-browser-warning"] = "true";
  
  const token = getDataFromLocalStorage("token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const originalMessage = error.response?.data?.message || error.message;
      if (!originalMessage || originalMessage === "Request failed with status code 403") {
        error.response.data.message = "Access denied: you do not have permission to perform this action.";
      }
    }
    return Promise.reject(error);
  }
);

const cache = new Map();

const cacheKey = (url, params) => {
  const sorted = params ? JSON.stringify(params, Object.keys(params).sort()) : "";
  return `${url}?${sorted}`;
};

export async function request(url, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const skipCache = options.skipCache || options.cache === false;

  if (method === "GET" && !skipCache) {
    const key = cacheKey(url, options.params);
    const entry = cache.get(key);
    if (entry) return entry;
  }

  try {
    const response = await apiClient({
      url,
      method,
      data: options.data,
      params: options.params,
      headers: options.headers,
    });

    const body = response.data;

    if (body && typeof body.status === "number" && body.status >= 400) {
      const normalizedError = new Error(body.message || "Request failed.");
      normalizedError.status = body.status;
      normalizedError.details = body;
      throw normalizedError;
    }

    if (method === "GET" && !skipCache) {
      const key = cacheKey(url, options.params);
      cache.set(key, body);
    } else {
      cache.clear();
    }

    return body;
  } catch (error) {
    const message = error.response?.data?.message || error.message || "An error occurred.";
    const normalizedError = new Error(message);
    normalizedError.status = error.response?.status || error.status;
    normalizedError.details = error.response?.data || error.details;
    throw normalizedError;
  }
}
